import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../Reveal";
import { MENU_ITEMS } from "../../lib/content";

export default function Menu() {
  const [active, setActive] = useState(0);
  const item = MENU_ITEMS[active];

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-cream py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-12 mb-16 md:mb-24">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="overline text-matcha-dark">— Chapter Two</span>
                <span className="h-px w-24 bg-ink/30" />
              </div>
              <h2
                className="font-serif font-light leading-[1] tracking-tight text-ink"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
              >
                The Menu,
                <br />
                <span className="italic font-cormorant text-matcha-dark">
                  thirteen studies in green.
                </span>
              </h2>
            </div>
            <p className="md:max-w-sm text-ink-soft text-base leading-relaxed">
              Each drink is a small composition — built from a single grade of
              matcha and only what it needs. Hover or tap to read the story
              behind the cup.
            </p>
          </div>
        </Reveal>

        {/* Two-column showcase: image + index list */}
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start">
          {/* Sticky image */}
          <div className="col-span-12 md:col-span-6 lg:col-span-5">
            <div className="md:sticky md:top-28">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-sage">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={item.img}
                    src={item.img}
                    alt={item.name}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute top-4 left-4">
                  <span className="overline text-bone bg-ink/40 backdrop-blur-md px-3 py-1.5 rounded-full">
                    N° {item.n}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/55 to-transparent p-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                    >
                      <h3 className="font-serif text-2xl md:text-3xl text-bone leading-tight">
                        {item.name}
                      </h3>
                      <p className="font-cormorant italic text-bone/80 text-sm mt-1">
                        {item.notes}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.p
                  key={item.description}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                  className="mt-6 font-cormorant text-xl md:text-2xl leading-snug text-ink/90"
                >
                  {item.description}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          {/* List */}
          <div className="col-span-12 md:col-span-6 lg:col-span-7">
            <ul className="border-t border-ink/15">
              {MENU_ITEMS.map((m, i) => (
                <li
                  key={m.n}
                  data-testid={`menu-item-${m.n}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`group cursor-pointer border-b border-ink/15 transition-all duration-500 ${
                    active === i ? "bg-bone" : ""
                  }`}
                >
                  <div className="flex items-center gap-6 md:gap-10 py-5 md:py-7 px-2 md:px-6">
                    <span
                      className={`overline w-10 transition-colors ${
                        active === i ? "text-matcha-dark" : "text-ink/40"
                      }`}
                    >
                      {m.n}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4
                        className={`font-serif text-2xl md:text-3xl leading-tight transition-all duration-500 ${
                          active === i
                            ? "text-ink translate-x-2"
                            : "text-ink/70"
                        }`}
                      >
                        {m.name}
                      </h4>
                      <p className="text-xs md:text-sm text-ink-soft mt-1 hidden md:block">
                        {m.notes}
                      </p>
                    </div>
                    <motion.div
                      animate={{
                        width: active === i ? 56 : 24,
                        opacity: active === i ? 1 : 0.4,
                      }}
                      className="h-px bg-ink shrink-0"
                    />
                  </div>
                </li>
              ))}
            </ul>

            <Reveal delay={0.1}>
              <div className="mt-8 p-6 md:p-8 bg-bone rounded-sm flex items-center justify-between gap-6">
                <div>
                  <p className="overline text-matcha-dark mb-2">A note</p>
                  <p className="font-cormorant italic text-lg md:text-xl text-ink/80 leading-snug">
                    Seasonal items and dessert pairings rotate monthly. Ask at
                    the counter for what's whisking today.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
